export type ClubMember = {
  handler_id: number;
  handler_name: string;
  email_address: string;
};

export type UpcomingTrainingDate = {
  group_name: string;
  training_date: string;
  days_until: number;
  notes?: string;
};

export type DefaultFeeBasis = "invoice" | "session";

export type TrainingGroup = {
  id?: number;
  training_group_id?: number;
  group_name: string;
  default_fee?: number;
  default_fee_basis?: DefaultFeeBasis;
  members: ClubMember[];
  upcoming_training_dates?: UpcomingTrainingDate[];
};

export type InvoiceByPeriodItem = {
  id: number;
  handler_id: number;
  handler_name: string;
  email_address: string;
  amount: number;
  paid: boolean;
  created_at: string;
  due_date: string;
  voided_at?: string | null;
};

export type InvoicesByPeriod = {
  period_start: string;
  period_end: string;
  group_name: string;
  total_invoices: number;
  paid_invoices: number;
  unpaid_invoices: number;
  total_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  invoices: InvoiceByPeriodItem[];
};

export type SlotLength = 30 | 60 | 90;

export type VenueHireSettings = {
  enabled: boolean;
  price_per_30_min: number;
  price_per_60_min: number;
  price_per_90_min: number;
  default_slot_length: SlotLength;
  notes?: string;
};

export type VenueAvailabilityWindow = {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  price_per_30_min: number;
  price_per_60_min: number;
  price_per_90_min: number;
};

export type VenueBlock = {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  reason?: string;
};

export type VenueSlotStatus = "available" | "booked" | "blocked";

export type VenueSlot = {
  date: string;
  start_time: string;
  end_time: string;
  slot_length?: SlotLength;
  price: number;
  status: VenueSlotStatus;
  booked_by?: {
    handler_id: number;
    handler_name: string;
  };
};

export type VenueBooking = {
  id: number;
  club_id: number;
  date: string;
  start_time: string;
  end_time: string;
  slot_length: SlotLength;
  price: number;
  handler_id: number;
  handler_name: string;
  invoice_id?: number;
  paid: boolean;
  created_at: string;
};

export type CreateVenueBookingResponse = {
  booking: VenueBooking;
  invoice_id: number;
  payment_reference: string;
  free?: boolean;
};

export type SharedVenueInfo = {
  club_id: number;
  club_name: string;
  venue_hire: VenueHireSettings;
  requires_name: boolean;
};

export type VenueHireShareAction = "enable" | "regenerate" | "disable";

export type OneOffSessionBookingMode = "off" | "private" | "public";

export type OneOffSessionParticipant = {
  id: number;
  handler_id: number;
  handler_name: string;
  email_address: string;
  fee: number;
  invoice_id?: number;
  invoice_paid?: boolean;
  self_booked?: boolean;
};

export type OneOffSession = {
  id: number;
  name: string;
  session_date: string;
  notes?: string;
  invoiced_at?: string;
  cancelled_at?: string;
  booking_mode?: OneOffSessionBookingMode;
  capacity?: number | null;
  fee?: number;
  share_token?: string | null;
  spaces_taken?: number;
  spaces_remaining?: number | null;
  participants: OneOffSessionParticipant[];
};

export type SharedOneOffSessionInfo = {
  club_id: number;
  club_name: string;
  session: OneOffSession;
  is_member: boolean;
  already_booked: boolean;
  my_participant_id?: number;
  my_invoice_id?: number;
  my_invoice_paid?: boolean;
  my_places_count?: number;
  my_invoice_ids?: number[];
};

export type BookOneOffSessionResponse = {
  participant_id: number;
  invoice_id: number;
  payment_reference: string;
  amount: number;
  paid: boolean;
  quantity?: number;
};

export type Club = {
  id: number;
  name: string;
  is_sandbox?: boolean;
  members: ClubMember[];
  training_groups: TrainingGroup[];
  one_off_sessions?: OneOffSession[];
  invoices_by_period?: InvoicesByPeriod[];
  venue_hire?: VenueHireSettings;
  venue_hire_share_token?: string | null;
  venue_hire_free_booking_code?: string | null;
};

export type Handler = {
  handler_id: number;
  handler_name: string;
  email_address: string;
};

export type InvoiceMemberData = {
  handler_id: number;
  handler_name: string;
  email_address: string;
  fee: string;
  feeEdited: boolean;
  excludedTrainingDates: string[];
};

export const getTrainingGroupId = (
  group: TrainingGroup,
): number | undefined => group.id ?? group.training_group_id;
